import Debug from 'debug';
import { diffSeconds } from '../../../shared/utils/date-time-utils'

interface QuestionTime {
  firstShownTime: Date | null
  seconds: number
}

function defaultQuestionTime(): QuestionTime {
  return { seconds: 0, firstShownTime: null }
}

type CurrentQuestion = {
  index: number
} & ({
  phase: 'shown'
  shownTime: Date
} | {
  phase: 'answered'
  shownTime: Date
  lastAnswerTime: Date
})

function shownCurrent(index: number, showTime: Date): CurrentQuestion {
  return {
    index: index,
    phase: 'shown',
    shownTime: showTime,
  }
}

const debug = Debug('quiz-question-time-state')

export type InputQuestionTimes = (QuestionTime | null)[]

export class QuizQuestionTimeState {

  private questionTimes: QuestionTime[] = []
  private current: CurrentQuestion | null = null

  constructor(questionCount: number) {
    this.questionTimes = [...new Array(questionCount)].map(_ => defaultQuestionTime())
  }

  load(input: InputQuestionTimes) {
    this.questionTimes = input.map(x => x || defaultQuestionTime())
  }
  
  reset(questionIndex: number, time: Date) {
    debug('reset', questionIndex)
    this.storeAndShownCurrent(questionIndex, time)
    this.questionTimes[questionIndex] = defaultQuestionTime()
    debug('times after reset', this.questionTimes)
  }

  shown(questionIndex: number, time: Date) {
    debug('shown', questionIndex, time)
    this.storeAndShownCurrent(questionIndex, time)
  }

  private storeAndShownCurrent(questionIndex: number, time: Date) {
    this.storeCurrent()
    this.current = shownCurrent(questionIndex, time)
  }

  answered(questionIndex: number, time: Date) {
    debug('answered', questionIndex, time)

    this.checkCurrent(questionIndex)
    if (!this.current) { return }

    this.current = {
      ...this.current,
      phase: 'answered',
      lastAnswerTime: time
    }
  }

  finished(questionIndex: number) {
    debug('finished', questionIndex)

    this.checkCurrent(questionIndex)
    if (!this.current) { return }

    this.storeAndClearCurrent()
  }

  finalize() {
    debug('finalize')

    this.storeAndClearCurrent()
  }

  private storeAndClearCurrent() {
    this.storeCurrent()
    this.current = null
  }

  questionTime(questionIndex: number) {
    const found = this.questionTimes[questionIndex]
    return {
      seconds: found.seconds,
      firstShownTime: found.firstShownTime
    }
  }

  get times() {
    return this.questionTimes
  }

  exportTimes() {
    return this.questionTimes.map(x => ({
      firstShownTime: x.firstShownTime,
      seconds: x.seconds
    }))
  }

  private checkCurrent(questionIndex: number) {
    if (!this.current) {
      throw new Error('Coding error: no current question has been set')
    }

    if (this.current.index !== questionIndex) {
      throw new Error(
        `Coding error: current question '${this.current.index}' and answer question '${questionIndex}' mismatch.`)
    }
  }

  private storeCurrent() {
    if (this.current && this.current.phase === 'answered') {
      const durationSeconds = diffSeconds(this.current.lastAnswerTime, this.current.shownTime)
      const index = this.current.index

      const found = this.questionTimes[index]
      this.questionTimes[index] = {
        seconds: found.seconds + durationSeconds,
        firstShownTime: found.firstShownTime || this.current.shownTime
      }

      debug(`current question stored at '${index}' with time '${durationSeconds} seconds', sum: '${this.questionTimes[index].seconds}' seconds, first shown: '${this.questionTimes[index].firstShownTime}'`,
            this.questionTimes)
    }
  }
}
